import { RRule } from "rrule";
import Axios from "axios";

import Colours, { pastelColours } from "./colours";
import Icons from "./icons";
import { dayjsUTC, addDaySuffix, months } from "./dates";
import { URLAPI } from "../configuration";

const dayjs = dayjsUTC;

export const CATEGORY = {
    bookingEngine: { label: "Booking engine", color: pastelColours[10] },
    email: { label: "Email", color: pastelColours[8] },
    marketing: { label: "Marketing", color: pastelColours[9] },
    ppc: { label: "PPC", color: pastelColours[1] },
    pr: { label: "PR", color: pastelColours[6] },
    reporting: { label: "Reporting", color: pastelColours[7] },
    seo: { label: "SEO", color: pastelColours[3] },
    socialMedia: { label: "Social media", color: pastelColours[2] },
    website: { label: "Website", color: pastelColours[0] },
    technical: { label: "Technical", color: pastelColours[5] },
    "GEO/AI": { label: "GEO/AI", color: pastelColours[11] },
    "Hotel Closure": { label: "Hotel Closure", color: pastelColours[12] },
    Seasonal: { label: "Seasonal", color: pastelColours[13] },
    Other: { label: "Other", color: pastelColours[14], order: 999 },
};

export const OWNER = {
    Hotel: { label: "Hotel" },
    Aro: { label: "Aro" },
};
export const EVENT_TYPE = {
    Action: { label: "Action" },
    Alert: { label: "Alert" },
};
export const PRIORITY = {
    high: {
        label: "High",
        Icon: Icons.ArrowUp,
        color: Colours.trafficLightRed,
        renderIcon: (sx = {}) => (
            <Icons.ArrowUp sx={{ ...sx, color: Colours.trafficLightRed }} />
        ),
    },
    medium: {
        label: "Medium",
        Icon: Icons.Horizontal,
        color: Colours.trafficLightYellow,
        renderIcon: (sx = {}) => (
            <Icons.Horizontal
                sx={{ ...sx, color: Colours.trafficLightYellow }}
            />
        ),
    },
    low: {
        label: "Low",
        Icon: Icons.ArrowDown,
        color: Colours.retain,
        renderIcon: (sx = {}) => (
            <Icons.ArrowDown sx={{ ...sx, color: Colours.retain }} />
        ),
    },
};

export const requestGetEvents = (hotelID, headers) =>
    Axios.get(`${URLAPI}/events/${hotelID}`, { headers });

export const requestGetContacts = (hotelID, headers) =>
    Axios.get(`${URLAPI}/hotels/contacts`, {
        params: { hotel_id: hotelID },
        headers,
    });

/**
 * Resolves the display label for an event Owner value.
 *  - "Aro" | "Hotel"  → returned as-is
 *  - a numeric string that matches a contact ID → "FirstName LastName"
 *  - anything else   → "—"
 *
 * @param {string|null} ownerValue  The raw Owner field from the event
 * @param {Object} contacts         Map of contactID → { FirstName, LastName }
 */
export const resolveOwnerLabel = (ownerValue, contacts = {}) => {
    if (ownerValue === "Aro" || ownerValue === "Hotel") return ownerValue;
    const contact = contacts[ownerValue];
    if (contact)
        return (
            `${contact.FirstName ?? ""} ${contact.LastName ?? ""}`.trim() || "—"
        );
    return "—";
};

export const requestGetUsers = (hotelID, headers) =>
    Axios.get(`${URLAPI}/users/${hotelID}`, { headers });

export const requestCreateEvent = (payload, headers) =>
    Axios({
        method: "post",
        url: `${URLAPI}/event/`,
        data: payload,
        headers,
    });

export const requestUpdateEvent = (eventID, payload, headers) =>
    Axios({
        method: "patch",
        url: `${URLAPI}/event/${eventID}`,
        data: payload,
        headers,
    });

export const requestDelete = async (
    { eventID, choice = "", exdate = null },
    headers,
) => {
    const params = {};
    if (choice) params.mode = choice;
    if (exdate) params.exclude_date = exdate;

    return Axios({
        method: "delete",
        url: `${URLAPI}/event/${eventID}`,
        params,
        headers,
    });
};

export const requestBulkDelete = async (events, headers) =>
    Axios({
        method: "delete",
        url: `${URLAPI}/events/delete-multiple`,
        data: { events },
        headers,
    });

export const buildBulkDeletePayload = (selectedEvents) =>
    selectedEvents.map((event) => {
        const isRecurring = Boolean(event.RepeatRule);
        if (isRecurring) {
            return {
                event_id: event.RecurrenceID ?? event.EventID,
                exclude_date: dayjs(event.FromDate).toISOString(),
            };
        }
        return { event_id: event.EventID };
    });

export const buildBulkDuplicatePayload = (event, hotelIds) => {
    const isRecurring = Boolean(event.RepeatRule);

    return {
        Name: event.Name,
        FromDate: dayjs(event.FromDate).format("YYYY-MM-DD"),
        ToDate: dayjs(event.ToDate).format("YYYY-MM-DD"),
        Category: event.Category,
        Subcategory: event.Subcategory ?? "",
        Description: event.Description,
        UserID: event.UserID,
        Owner: event.Owner,
        HotelIDs: hotelIds,
        RepeatRule: isRecurring
            ? updateStartEndRRule(dayjs(event.FromDate), null, event.RepeatRule)
            : null,
        RepeatStart: isRecurring
            ? dayjs(event.FromDate).format("YYYY-MM-DD")
            : null,
        RepeatEnd:
            isRecurring && event.RepeatEnd
                ? dayjs(event.RepeatEnd).format("YYYY-MM-DD")
                : null,
        ImpactStart: event.ImpactStart
            ? dayjs(event.ImpactStart).format("YYYY-MM-DD")
            : null,
        ImpactEnd: event.ImpactEnd
            ? dayjs(event.ImpactEnd).format("YYYY-MM-DD")
            : null,
        ExpHeadcount: event.ExpHeadcount ?? null,
        ExpRoomNights: event.ExpRoomNights ?? null,
        ExpAvgRate: event.ExpAvgRate ?? null,
        ExpRevenue: event.ExpRevenue ?? null,
    };
};

export const requestBulkDuplicate = (event, hotelIds, headers) =>
    Axios({
        method: "post",
        url: `${URLAPI}/events/duplicate-multiple`,
        data: buildBulkDuplicatePayload(event, hotelIds),
        headers,
    });

export const buildRRule = (data, oldRRule = "") => {
    const { startDate, repeatEvery, repeatUnit, options, endDate } = data;

    const exdates = oldRRule
        .split("\n")
        .filter((line) => line.startsWith("EXDATE:"));

    const ruleOptions = {
        freq: RRule.DAILY,
        interval: repeatEvery || 1,
        dtstart: startDate ? dayjs(startDate).toDate() : new Date(),
    };

    // --- Frequency ---
    switch (repeatUnit) {
        case "Day":
            ruleOptions.freq = RRule.DAILY;
            break;
        case "Week":
            ruleOptions.freq = RRule.WEEKLY;
            if (options?.weeklyDays?.length) {
                const map = {
                    Monday: RRule.MO,
                    Tuesday: RRule.TU,
                    Wednesday: RRule.WE,
                    Thursday: RRule.TH,
                    Friday: RRule.FR,
                    Saturday: RRule.SA,
                    Sunday: RRule.SU,
                };
                ruleOptions.byweekday = options.weeklyDays.map((d) => map[d]);
            }
            break;
        case "Month":
            ruleOptions.freq = RRule.MONTHLY;
            if (
                options?.monthlyOption === "dayOfMonth" ||
                options?.monthlyOption === "lastDay"
            ) {
                ruleOptions.bymonthday =
                    options?.monthlyOption === "lastDay" ? -1 : options.day;
            } else if (
                options?.monthlyOption === "weekdayPattern" ||
                options?.monthlyOption === "lastWeekDay"
            ) {
                const map = {
                    Monday: RRule.MO,
                    Tuesday: RRule.TU,
                    Wednesday: RRule.WE,
                    Thursday: RRule.TH,
                    Friday: RRule.FR,
                    Saturday: RRule.SA,
                    Sunday: RRule.SU,
                };
                if (options?.monthlyOption === "lastWeekDay") {
                    ruleOptions.bysetpos = -1;
                    ruleOptions.byweekday = map[options.pattern.day];
                } else {
                    let w = options.pattern.week;
                    ruleOptions.bysetpos = w;
                    ruleOptions.byweekday = map[options.pattern.day];
                }
            }
            break;
        case "Year":
            ruleOptions.freq = RRule.YEARLY;
            if (options?.yearlyOption === "specificDate" && options.date) {
                ruleOptions.bymonth = dayjs(options.date).month() + 1; // 1-based
                ruleOptions.bymonthday = dayjs(options.date).date();
            } else if (options?.yearlyOption === "weekdayPattern") {
                const map = {
                    Monday: RRule.MO,
                    Tuesday: RRule.TU,
                    Wednesday: RRule.WE,
                    Thursday: RRule.TH,
                    Friday: RRule.FR,
                    Saturday: RRule.SA,
                    Sunday: RRule.SU,
                };
                ruleOptions.bysetpos = options.pattern.week;
                ruleOptions.byweekday = map[options.pattern.day];
                ruleOptions.bymonth = months.indexOf(options.pattern.month) + 1;
            }
            break;
        default:
            break;
    }

    // --- End Condition ---
    ruleOptions.until = dayjs(endDate).toDate();

    const rrule = new RRule(ruleOptions);

    return exdates.length
        ? rrule.toString() + "\n" + exdates[0]
        : rrule.toString();
};

// RRULE day map
const dayMap = {
    MO: "Mon",
    TU: "Tue",
    WE: "Wed",
    TH: "Thu",
    FR: "Fri",
    SA: "Sat",
    SU: "Sun",
};

const removeRRuleExdates = (rrule) =>
    rrule
        .split("\n")
        .filter((line) => !line.startsWith("EXDATE:"))
        .join("\n");

export const describeRRule = (rruleString, eventStart) => {
    try {
        const rrule = removeRRuleExdates(rruleString);
        const rule = RRule.fromString(rrule);
        const options = rule.origOptions;

        const parts = [];

        // --- Frequency ---
        const freqMap = {
            [RRule.DAILY]: "day",
            [RRule.WEEKLY]: "week",
            [RRule.MONTHLY]: "month",
            [RRule.YEARLY]: "year",
        };

        const freqWord = freqMap[options.freq] || "period";
        const interval = options.interval || 1;
        const intervalText =
            interval === 1
                ? `Repeats every ${freqWord}`
                : `Repeats every ${interval} ${freqWord}s`;

        parts.push(intervalText);

        // --- BYDAY / BYMONTHDAY / BYSETPOS ---
        if (options.freq === RRule.WEEKLY && options.byweekday?.length) {
            const days = options.byweekday.map((d) => {
                const key = typeof d === "string" ? d : d.toString();
                return dayMap[key];
            });
            parts.push(`on ${days.join(", ")}`);
        }

        if (options.freq === RRule.MONTHLY) {
            if (options.bymonthday) {
                const day = options.bymonthday;
                if (day < 0) {
                    parts.push("on the last day");
                } else {
                    parts.push(`on the ${addDaySuffix(day)}`);
                }
            } else if (options.bysetpos && options.byweekday) {
                const weekNum = options.bysetpos;
                const weekday = Array.isArray(options.byweekday)
                    ? options.byweekday[0].toString()
                    : options.byweekday.toString();
                if (weekNum < 0) {
                    parts.push(`on the last ${dayMap[weekday.slice(0, 2)]}`);
                } else {
                    parts.push(
                        `on the ${addDaySuffix(weekNum)} ${
                            dayMap[weekday.slice(0, 2)]
                        }`,
                    );
                }
            }
        }

        if (options.freq === RRule.YEARLY) {
            if (options.bymonth && options.bymonthday) {
                const month = dayjs()
                    .month(options.bymonth - 1)
                    .format("MMMM");
                parts.push(`on ${month} ${addDaySuffix(options.bymonthday)}`);
            } else if (
                options.bysetpos &&
                options.byweekday &&
                options.bymonth
            ) {
                const month = dayjs()
                    .month(options.bymonth - 1)
                    .format("MMMM");
                const weekday = Array.isArray(options.byweekday)
                    ? options.byweekday[0].toString()
                    : options.byweekday.toString();
                parts.push(
                    `on the ${addDaySuffix(options.bysetpos)} ${
                        dayMap[weekday.slice(0, 2)]
                    } of ${month}`,
                );
            }
        }

        // --- END CONDITIONS ---
        // const endDate = getRRuleEndDate(rrule);
        let resultRRule = parts.join(", ");
        if (options.until) {
            resultRRule =
                resultRRule +
                `\nEnds on ${dayjs(options.until).format("D MMM YYYY")}`;
        }

        if (eventStart && options.dtstart) {
            resultRRule =
                `This event runs on ${dayjs(eventStart).format("D MMM YYYY")}\n` +
                resultRRule;
        }
        return resultRRule;
    } catch (err) {
        console.error("Invalid RRULE:", err);
        return "Invalid recurrence rule";
    }
};

export const rruleToEvent = (rruleString) => {
    try {
        const rrule = removeRRuleExdates(rruleString);
        const rule = RRule.fromString(rrule);
        const opt = rule.origOptions;

        const event = {
            startDate: opt.dtstart ? dayjs(opt.dtstart) : dayjs(),
            repeatEvery: opt.interval || 1,
            repeatUnit: "Day",
            options: {},
            endDate: dayjs(opt.until),
        };

        // --- Frequency ---
        switch (opt.freq) {
            case RRule.DAILY:
                event.repeatUnit = "Day";
                break;

            case RRule.WEEKLY:
                event.repeatUnit = "Week";
                if (opt.byweekday?.length) {
                    event.options.weeklyDays = opt.byweekday.map(
                        (d) => d.toString().slice(0, 2), // "MO", "TU"...
                    );
                    // convertir "MO" → "Monday", etc.
                    const map = {
                        MO: "Monday",
                        TU: "Tuesday",
                        WE: "Wednesday",
                        TH: "Thursday",
                        FR: "Friday",
                        SA: "Saturday",
                        SU: "Sunday",
                    };
                    event.options.weeklyDays = event.options.weeklyDays.map(
                        (d) => map[d] || d,
                    );
                }
                break;

            case RRule.MONTHLY:
                event.repeatUnit = "Month";
                if (opt.bymonthday) {
                    if (opt.bymonthday === -1) {
                        event.options.monthlyOption = "lastDay";
                    } else {
                        event.options.monthlyOption = "dayOfMonth";
                        event.options.day = opt.bymonthday;
                    }
                } else if (opt.bysetpos && opt.byweekday) {
                    const weekday = Array.isArray(opt.byweekday)
                        ? opt.byweekday[0].toString().slice(0, 2)
                        : opt.byweekday.toString().slice(0, 2);
                    const map = {
                        MO: "Monday",
                        TU: "Tuesday",
                        WE: "Wednesday",
                        TH: "Thursday",
                        FR: "Friday",
                        SA: "Saturday",
                        SU: "Sunday",
                    };
                    const weekNum = opt.bysetpos;
                    const dayName = map[weekday];

                    event.options.monthlyOption =
                        weekNum === -1 ? "lastWeekDay" : "weekdayPattern";
                    event.options.pattern = {
                        week: weekNum,
                        day: dayName,
                    };
                }
                break;

            case RRule.YEARLY:
                event.repeatUnit = "Year";
                if (opt.bymonth && opt.bymonthday) {
                    event.options.yearlyOption = "specificDate";
                    const date = dayjs()
                        .month(opt.bymonth - 1)
                        .date(opt.bymonthday)
                        .year(dayjs(opt.dtstart).year());
                    event.options.date = date;
                } else if (opt.bysetpos && opt.byweekday && opt.bymonth) {
                    const weekday = Array.isArray(opt.byweekday)
                        ? opt.byweekday[0].toString().slice(0, 2)
                        : opt.byweekday.toString().slice(0, 2);
                    const map = {
                        MO: "Monday",
                        TU: "Tuesday",
                        WE: "Wednesday",
                        TH: "Thursday",
                        FR: "Friday",
                        SA: "Saturday",
                        SU: "Sunday",
                    };
                    const months = [
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                    ];
                    event.options.yearlyOption = "weekdayPattern";
                    event.options.pattern = {
                        week: opt.bysetpos,
                        day: map[weekday],
                        month: months[opt.bymonth - 1],
                    };
                }
                break;

            default:
                break;
        }

        return event;
    } catch (err) {
        console.error("Error parsing RRULE:", err);
        return null;
    }
};

export const getRRuleEndDate = (rruleString) => {
    try {
        const rrule = removeRRuleExdates(rruleString);
        const rule = RRule.fromString(rrule);
        const opts = rule.origOptions;

        // if UNTIL, return it
        if (opts.until) {
            return dayjs(opts.until);
        }

        // Si no tiene UNTIL ni COUNT → es infinito.
        return null;
    } catch (err) {
        console.error("Error getting RRULE end date:", err);
        return null;
    }
};

export const updateStartEndRRule = (start, end, rrule) => {
    const rruleData = rruleToEvent(rrule);
    if (start) rruleData.startDate = start;
    if (end) rruleData.endDate = end;

    const newRRule = buildRRule(rruleData, rrule);
    return newRRule;
};

export const getOccurrences = (rruleString, start, end, repeatStart = null) => {
    if (rruleString === "") return [];
    try {
        const lines = rruleString.split("\n");
        const exdateLines = lines.filter((l) => l.startsWith("EXDATE:"));

        const exdates = exdateLines.flatMap((ex) =>
            ex
                .replace("EXDATE:", "")
                .split(",")
                .map((d) => d.substr(0, 8)),
        );

        const cleanRRule = removeRRuleExdates(rruleString);
        const rule = RRule.fromString(cleanRRule);

        const durationDays = dayjs(end).diff(dayjs(start), "day") + 1;

        let occurrences = rule.all().map((d) => dayjs(d));

        if (repeatStart) {
            const originalStart = dayjs(repeatStart);
            const isOriginalIncluded = occurrences.some((occ) =>
                occ.isSame(originalStart, "day"),
            );

            if (!isOriginalIncluded) {
                occurrences = [originalStart, ...occurrences];
            }
        }

        return occurrences.map((occ, index) => ({
            index: index + 1,
            date: occ.format("DD MMM YYYY"),
            rawDate: occ,
            durationDays,
            isExcluded: exdates.includes(occ.format("YYYYMMDD")),
        }));
    } catch (err) {
        console.error("Invalid RRULE:", err);
        return [];
    }
};

export const findOccurrence = (rruleString, start, end, repeatStart = null) => {
    if (!rruleString) return null;

    const occurrences = getOccurrences(rruleString, start, end, repeatStart);

    const startDate = dayjs(start);
    const endDate = dayjs(end);

    const totalOccs = occurrences.length;

    for (const occ of occurrences) {
        const occStart = occ.rawDate;
        const occEnd = occStart.add(occ.durationDays - 1, "day");

        const matches =
            startDate.isSame(occStart, "day") && endDate.isSame(occEnd, "day");

        if (matches) {
            return {
                ...occ,
                totalOccs,
            };
        }
    }

    return null;
};

export const getRecurrenceType = (rruleString) => {
    if (!rruleString) return null;

    const rruleLine = rruleString
        .split("\n")
        .find((l) => l.startsWith("RRULE:"));

    if (!rruleLine) return null;

    const ruleBody = rruleLine.replace("RRULE:", "");

    const freqPart = ruleBody.split(";").find((p) => p.startsWith("FREQ="));

    if (!freqPart) return null;

    const freq = freqPart.replace("FREQ=", "").trim();

    switch (freq) {
        case "DAILY":
            return "Daily";
        case "WEEKLY":
            return "Weekly";
        case "MONTHLY":
            return "Monthly";
        case "YEARLY":
            return "Yearly";
        default:
            return freq;
    }
};

// Fetches events, contacts, and users for a hotel in a single call.
// Returns Events:{data, contacts, users} ready to store in dataSimple.
export const fetchEventsBundle = async (hotelID, headers) => {
    const [resEvents, resContacts, resUsers] = await Promise.all([
        requestGetEvents(hotelID, headers),
        requestGetContacts(hotelID, headers),
        requestGetUsers(hotelID, headers),
    ]);

    const validUsers = Array.from(
        new Map(
            resUsers.data.data
                .filter((u) => u.user_id !== 0)
                .map((u) => [u.user_id, u]),
        ).values(),
    );

    const uniqueContacts = Array.from(
        new Map(resContacts.data.data.map((u) => [u.AccountID, u])).values(),
    );

    return {
        Events: {
            ...resEvents.data,
            contacts: uniqueContacts.reduce((acc, u) => {
                acc[u.AccountID] = u;
                return acc;
            }, {}),
            users: validUsers.reduce((acc, u) => {
                acc[u.user_id] = u;
                return acc;
            }, {}),
        },
    };
};

// Groups pre-expanded Events occurrences by FromDate, converting date strings to Date objects.
export const processEvents = (data) => {
    let auxEvents = {};

    data.forEach((event) => {
        const fromDate = new Date(event.FromDate);
        const toDate = new Date(event.ToDate);
        const dateString = fromDate.toISOString().slice(0, 10);

        let auxEvent = { ...event, FromDate: fromDate, ToDate: toDate };

        if (auxEvents.hasOwnProperty(dateString)) {
            auxEvents[dateString].push(auxEvent);
        } else {
            auxEvents[dateString] = [auxEvent];
        }
    });

    return auxEvents;
};
